import { formatDate } from '@/lib/format/date';
import {
  appendFormAttachments,
  buildContractFiltersInput,
  processDiscrepancyItems,
  processExistingAttachments,
  processFilterOptions,
} from '@/lib/mappers';
import { formatTextOrDash } from '@/lib/format/text';
import { formatNumber, formatPercent, formatQuantityOrDash } from '@/lib/format/number';
import { unitSymbol } from '@/lib/mappers/units';
import { formValue } from '@/lib/mappers/formValues';
import { splitReferenceId } from '@/lib/utils/id';
import { REMOVED_LINKS } from '@/lib/utils/form';
import {
  buildQuantitiesByUnit,
  buildRawQuantitiesByUnit,
  defaultUnitType,
} from '@/lib/mappers/quantities';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { CONTRACT_SORT_FIELDS } from '../constants/contracts.constants';

import { MULTI_SITE_TRANSFER } from '../constants/contractFormOptions';
import {
  ALLOCATION_PREFERENCE,
  FACILITY_KINDS,
  allocationPreferenceLabel,
  buildFacilityValue,
  feedstockCategoryLabel,
  parseFacilityValue,
  toAllocationPreferenceArray,
} from '@/config/formOptions';
import { contractStatusLabel, shipmentStatusLabel } from '@/config/statuses';

// DTO ⇄ view model. build… = to server, process… = to screen.

// The server ORs ContractSearch's keys, so one box searches all three.
const buildContractSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  const text = { contains: term, mode: 'insensitive' };
  return { contractId: text, supplierAccount: { companyname: text }, material: { name: text } };
};

export const buildContractsQuery = ({
  page,
  pageSize,
  sortBy,
  sortDir,
  search,
  extraFilters,
  ...filters
} = {}) => {
  const sortField = CONTRACT_SORT_FIELDS[sortBy];
  return {
    page,
    limit: pageSize,
    // An unsupported sort field would fail the query.
    orderBy: sortField ? [{ field: sortField, direction: sortDir ?? 'asc' }] : undefined,
    search: buildContractSearchInput(search),
    filters: buildContractFiltersInput(filters, extraFilters),
  };
};

/**
 * The contract stores no `working_unit` when it was created without a factor, but the
 * material still has one and the server derives the quantities from a linked shipment's
 * density. Mirrors the server's own getWorkingUnit: unit_name is the working unit only
 * where it differs from converted_unit_name.
 */
export const workingUnitOf = (contract) => {
  if (contract?.workingUnit) return contract.workingUnit;
  const displayUnit = contract?.material?.displayUnits?.[0];
  const unit = displayUnit?.unitName ?? null;
  return unit && unit !== displayUnit?.convertedUnitName ? unit : null;
};

// One table row, display-ready.
export const processContract = (contract) => {
  const unitsByType = {
    [UNIT_TYPES.working]: {
      unit: workingUnitOf(contract),
      amounts: {
        contractedQuantity: contract.workingQuantity,
        shippedQuantity: contract.shippedWorkingQuantity,
        outstandingQuantity: contract.outstandingWorkingQuantity,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: contract.declarationUnit,
      amounts: {
        contractedQuantity: contract.declarationQuantity,
        shippedQuantity: contract.shippedDeclarationQuantity,
        outstandingQuantity: contract.outstandingDeclarationQuantity,
      },
    },
  };
  const byUnit = buildQuantitiesByUnit(unitsByType);

  return {
    id: contract.id,
    contractId: contract.contractId ?? '',
    status: contractStatusLabel(contract.status),
    // The raw enum too: the row actions gate on it — see constants/contractActions.js.
    statusCode: contract.status ?? null,
    materialName: contract.material?.name ?? '',
    totalShipmentLinked: contract.linkedShipmentCount ?? 0,
    supplierName: contract.supplierAccount?.companyname ?? '',
    facilityName: contract.site?.siteName ?? '',
    terminal: contract.terminal ?? '',
    byUnit,
    // The link wizard deducts what step 2 assigns from outstanding, which needs the number.
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
    unitType: defaultUnitType(byUnit),
    ghgEmissions: contract.ghgEmissions == null ? '' : formatNumber(contract.ghgEmissions),
    ghgEmissionSavings:
      contract.ghgEmissionSavings == null ? '' : formatPercent(contract.ghgEmissionSavings),
    rawMaterialType: contract.rawMaterialType ?? '',
    countryOfOrigin: contract.countryOfOrigin ?? '',
    feedstockCategory: feedstockCategoryLabel(contract.feedstockCategory),
    // A bare array would render as "ESTONIAPORTUGALITALY".
    allocationPreference: toAllocationPreferenceArray(contract.preferredAllocation)
      .map(allocationPreferenceLabel)
      .join(', '),
    deliverBy: formatDate(contract.deliverBy),
    discrepancyCount: contract.discrepancyCount ?? 0,
  };
};

export const processContractFilterOptions = (result) => ({
  materialOptions: processFilterOptions(result?.materials),
  supplierOptions: processFilterOptions(result?.suppliers),
});

export const processContractsPage = (result) => ({
  rows: (result?.nodes ?? []).map(processContract),
  totalCount: result?.pageInfo?.total ?? 0,
});

const processAttachment = (attachment, index) => ({
  id: attachment.id,
  fileId: attachment.file?.id,
  serialNo: index + 1,
  name: formatTextOrDash(attachment.file?.filename),
  lastUpdatedOn: formatTextOrDash(formatDate(attachment.file?.updatedAt, 'dd MMM yyyy | HH:mm')),
});

// Contract Details — Overview tab, meta bar, header discrepancy modal and Attachments.
export const processContractDetail = (contract) => {
  const declarationUnit = unitSymbol(contract.declarationUnit);
  const workingUnit = unitSymbol(workingUnitOf(contract));

  return {
    id: contract.id,
    contractId: formatTextOrDash(contract.contractId),
    materialName: formatTextOrDash(contract.material?.name),
    supplierName: formatTextOrDash(contract.supplierAccount?.companyname),
    facilityName: formatTextOrDash(contract.site?.siteName),
    terminal: formatTextOrDash(contract.terminal),
    transportName: formatTextOrDash(contract.transport),
    vesselName: formatTextOrDash(contract.vessel),
    dispatchBy: formatTextOrDash(formatDate(contract.deliverBy)),
    statusCode: contract.status,
    statusLabel: formatTextOrDash(contractStatusLabel(contract.status)),
    // The header's actions gate on it, exactly as the list row's do.
    totalShipmentLinked: contract.linkedShipmentCount ?? 0,
    // Why the working column is dashes, when it is — the server writes the copy.
    workingUnitMessage:
      contract.workingUnitStatus === 'AVAILABLE' ? null : (contract.workingUnitMessage ?? null),
    declarationUnit,
    workingUnit,
    declarationQuantities: {
      contracted: formatQuantityOrDash(contract.declarationQuantity, declarationUnit),
      shipped: formatQuantityOrDash(contract.shippedDeclarationQuantity, declarationUnit),
      outstanding: formatQuantityOrDash(contract.outstandingDeclarationQuantity, declarationUnit),
    },
    workingQuantities: {
      contracted: formatQuantityOrDash(contract.workingQuantity, workingUnit),
      shipped: formatQuantityOrDash(contract.shippedWorkingQuantity, workingUnit),
      outstanding: formatQuantityOrDash(contract.outstandingWorkingQuantity, workingUnit),
    },
    // The link wizard deducts what step 2 assigns from outstanding, which needs the number.
    rawQuantities: {
      [UNIT_TYPES.declaration]: {
        unit: declarationUnit,
        outstanding: contract.outstandingDeclarationQuantity,
      },
      [UNIT_TYPES.working]: {
        unit: workingUnit,
        outstanding: contract.outstandingWorkingQuantity,
      },
    },
    ghgEmissions: contract.ghgEmissions == null ? '-' : formatNumber(contract.ghgEmissions),
    ghgEmissionSavings:
      contract.ghgEmissionSavings == null ? '-' : formatPercent(contract.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(contract.rawMaterialType),
    countryOfOrigin: formatTextOrDash(contract.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(contract.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(contract.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    discrepancyCount: contract.discrepancyCount ?? 0,
    discrepancies: processDiscrepancyItems(contract.discrepancies?.items),
    attachments: (contract.attachments ?? []).map(processAttachment),
  };
};

// The discrepancy popup's view model; the field names match processContractDetail's.
export const processContractDiscrepancy = (contract) => {
  const workingUnit = unitSymbol(workingUnitOf(contract));

  return {
    id: contract?.id,
    contractId: formatTextOrDash(contract?.contractId),
    status: contract?.status,
    statusLabel: formatTextOrDash(contractStatusLabel(contract?.status)),
    materialName: formatTextOrDash(contract?.material?.name),
    supplierName: formatTextOrDash(contract?.supplierAccount?.companyname),
    facilityName: formatTextOrDash(contract?.site?.siteName),
    terminal: formatTextOrDash(contract?.terminal),
    dispatchBy: formatTextOrDash(formatDate(contract?.deliverBy)),
    workingQuantities: {
      contracted: formatQuantityOrDash(contract?.workingQuantity, workingUnit),
      shipped: formatQuantityOrDash(contract?.shippedWorkingQuantity, workingUnit),
      outstanding: formatQuantityOrDash(contract?.outstandingWorkingQuantity, workingUnit),
    },
    discrepancies: processDiscrepancyItems(contract?.discrepancies?.items),
  };
};

// One row of the Linked Incoming Shipments tab.
const processLinkedShipment = (node, contract, index) => {
  const shipment = node.shipment ?? {};
  // Not buildQuantitiesByUnit: actualShipmentQty arrives in the shipment's own unit.
  const quantitiesByUnit = {
    [UNIT_TYPES.working]: {
      unit: contract.workingUnit,
      assignedQty: formatQuantityOrDash(node.assignedQuantityWorking, contract.workingUnit),
      actualShipmentQty: formatQuantityOrDash(
        shipment.shipmentQuantityWorking,
        shipment.workingUnit,
      ),
      unassignedQty: formatQuantityOrDash(
        shipment.unassignedContractQuantityWorking,
        contract.workingUnit,
      ),
      pendingQty: formatQuantityOrDash(
        shipment.pendingShipmentQuantityWorking,
        contract.workingUnit,
      ),
      availableQty: formatQuantityOrDash(
        shipment.availableShipmentQuantityWorking,
        contract.workingUnit,
      ),
    },
    [UNIT_TYPES.declaration]: {
      unit: contract.declarationUnit,
      assignedQty: formatQuantityOrDash(node.assignedQuantityDeclaration, contract.declarationUnit),
      actualShipmentQty: formatQuantityOrDash(
        shipment.shipmentQuantityDeclaration,
        contract.declarationUnit,
      ),
      unassignedQty: formatQuantityOrDash(
        shipment.unassignedContractQuantityDeclaration,
        contract.declarationUnit,
      ),
      pendingQty: formatQuantityOrDash(
        shipment.pendingShipmentQuantityDeclaration,
        contract.declarationUnit,
      ),
      availableQty: formatQuantityOrDash(
        shipment.availableShipmentQuantityDeclaration,
        contract.declarationUnit,
      ),
    },
  };
  const byUnit = Object.fromEntries(
    Object.entries(quantitiesByUnit).filter(([, quantities]) => Boolean(quantities.unit)),
  );

  return {
    id: node.linkId,
    // The unlink route takes the shipment's own id, not the link row's — see contractsApi.js.
    shipmentDbId: shipment.id,
    // Raw, not formatted: the contract's derived conversion compares these numerically.
    density: shipment.density ?? null,
    serialNo: index + 1,
    shipmentId: formatTextOrDash(shipment.shipmentId),
    linkedContract: formatTextOrDash((node.linkedContractIds ?? []).join(', ')),
    shipmentStatus: formatTextOrDash(shipmentStatusLabel(shipment.status)),
    terminal: formatTextOrDash(shipment.terminal),
    unitType: defaultUnitType(byUnit),
    byUnit,
    ghgEmissions: shipment.ghgEmissions == null ? '-' : formatNumber(shipment.ghgEmissions),
    ghgEmissionSavings:
      shipment.ghgEmissionSavings == null ? '-' : formatPercent(shipment.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(shipment.rawMaterialType),
    countryOfOrigin: formatTextOrDash(shipment.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(shipment.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(shipment.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dateOfDispatch: formatTextOrDash(formatDate(shipment.dateOfDispatch)),
    discrepancies: shipment.discrepancyCount ?? 0,
    discrepancyItems: processDiscrepancyItems(shipment.discrepancies),
  };
};

export const processLinkedShipments = (result, contract) =>
  (result?.nodes ?? []).map((node, index) => processLinkedShipment(node, contract, index));

// A contract's two boxes concatenate with NO separator, unlike every other reference id.
const joinContractId = ({ idPrefix, idNumber }) => `${idPrefix ?? ''}${idNumber ?? ''}`;

// Either a site or a back-to-back account, never both.
const processFacilityId = (contract) => {
  if (contract.backToBackAccountId != null) {
    return buildFacilityValue(FACILITY_KINDS.backToBack, contract.backToBackAccountId);
  }
  if (contract.site?.id != null) {
    return buildFacilityValue(FACILITY_KINDS.site, contract.site.id);
  }
  return '';
};

// The form's values — raw, unlike `processContract`'s display strings.
export const processContractForm = (contract) => ({
  ...splitReferenceId(contract.contractId),
  facilityId: processFacilityId(contract),
  terminalId: formValue(contract.terminalRefId),
  // Inverse of single_counting — see contracts/CLAUDE.md.
  multiSiteTransfer: contract.singleCounting ? MULTI_SITE_TRANSFER.no : MULTI_SITE_TRANSFER.yes,
  supplierId: formValue(contract.supplierAccount?.id),
  materialId: formValue(contract.material?.id),
  deliverBy: formatDate(contract.deliverBy, 'yyyy-MM-dd'), // the native date input's format
  transportName: formValue(contract.transport),
  vesselName: formValue(contract.vessel),
  contractedQuantity: formValue(contract.contractedQuantity),
  // GraphQL sends this one unmapped, so the value must be symbolised to match the options.
  unit: formValue(unitSymbol(contract.contractedQuantityUnit)),
  density: formValue(contract.conversionFactor),
  // The server derives the working-unit answer; the form only renders it.
  workingUnitStatus: contract.workingUnitStatus ?? null,
  workingUnitMessage: contract.workingUnitMessage ?? null,
  linkedShipments: (contract.linkedShipments ?? []).map((link) => ({
    id: link.id,
    shipmentDbId: link.batchGroup?.id ?? null,
    shipmentId: link.batchGroup?.shipmentId ?? '',
  })),
  // Chips the user removes land here and are unlinked by Save, never before it.
  [REMOVED_LINKS]: [],
  ghgEmissions: formValue(contract.ghgEmissions),
  ghgEmissionSavings: formValue(contract.ghgEmissionSavings),
  rawMaterialType: formValue(contract.rawMaterialType),
  countryOfOrigin: formValue(contract.countryOfOrigin),
  feedstockCategory: formValue(contract.feedstockCategory),
  // The server sends an array despite the schema typing it String.
  allocationPreference: toAllocationPreferenceArray(contract.preferredAllocation).filter(
    (value) => value !== ALLOCATION_PREFERENCE.singleCounting,
  ),
  attachments: [],
  existingAttachments: processExistingAttachments(contract.attachments),
  removeFileIds: [],
});

// Multipart for the shared attachment fields; the server reads "" as "clear".
export const buildContractFormData = (values, { equivalentUnit } = {}) => {
  const facility = parseFacilityValue(values.facilityId);
  const body = new FormData();

  body.append('contractId', joinContractId(values));
  body.append(
    facility.kind === FACILITY_KINDS.backToBack ? 'backToBackAccountId' : 'siteId',
    facility.id ?? '',
  );
  body.append('terminalId', facility.kind === FACILITY_KINDS.site ? values.terminalId : '');
  // Omitting this leaves the old terminal's name behind when the id clears.
  body.append('terminal', '');
  body.append('supplierAccountId', values.supplierId);
  body.append('materialId', values.materialId);
  body.append('deliverBy', values.deliverBy);
  body.append('transport', values.transportName);
  body.append('vessel', values.vesselName);
  body.append('contractedQuantity', values.contractedQuantity);
  body.append('contractedQuantityUnit', values.unit);
  body.append('ghgEmissions', values.ghgEmissions);
  body.append('ghgEmissionSavings', values.ghgEmissionSavings);
  body.append('rawMaterialType', values.rawMaterialType);
  body.append('countryOfOrigin', values.countryOfOrigin);
  body.append('feedstockCategory', values.feedstockCategory);
  body.append('preferredAllocation', JSON.stringify(values.allocationPreference ?? []));
  body.append('singleCounting', String(values.multiSiteTransfer !== MULTI_SITE_TRANSFER.yes));

  // TODO: clearing a saved density needs a server change — it reads "" as "unchanged".
  if (values.density && equivalentUnit) {
    body.append('density', values.density);
    body.append('equivalentUnit', equivalentUnit);
  }

  appendFormAttachments(body, values);

  return body;
};
